<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import {
  useCreateDocumentSpaceSectionMutation,
  useCreateSpaceSectionAfterMutation,
  Maybe,
} from "@/graphql/operations";
import { Modal } from "@/components";

const props = defineProps({
  documentSpaceId: {
    type: String,
    required: false,
  },
  afterSectionId: {
    type: String,
    required: false,
  },
  sectionNames: {
    type: Array<Maybe<string> | undefined>,
    required: true,
  },
});

const emits = defineEmits(["success", "close"]);

const inputElem = ref();

onMounted(() => {
  if (!inputElem.value) {
    return;
  }

  inputElem.value.focus();
});

const newName = ref<string>("");

const createError = ref(false);
const { executeMutation, fetching: creatingSection } =
  useCreateDocumentSpaceSectionMutation();
const { executeMutation: createAfter, fetching: creatingAfter } =
  useCreateSpaceSectionAfterMutation();
const creating = computed(() => creatingSection.value || creatingAfter.value);
const canSubmit = computed(
  () =>
    !creating.value &&
    newName.value.length > 0 &&
    !props.sectionNames.includes(newName.value),
);
const submit = async () => {
  if (!canSubmit.value) return;
  createError.value = false;
  if (!props.documentSpaceId && !props.afterSectionId) {
    return;
  }

  let res;
  if (props.documentSpaceId) {
    res = await executeMutation({
      spaceId: props.documentSpaceId,
      title: newName.value,
    });
  } else {
    res = await createAfter({
      afterSectionId: props.afterSectionId!,
      title: newName.value,
    });
  }

  const payload = res?.data;
  const created =
    payload &&
    ("createDocumentSpaceSection" in payload
      ? payload.createDocumentSpaceSection
      : "insertSpaceSectionAfter" in payload &&
        payload.insertSpaceSectionAfter);
  if (res!.error || !created) {
    createError.value = true;
  } else {
    emits("close");
    emits("success");
  }
};

const submitOnEnter = (event: KeyboardEvent) => {
  // Safari can end composition before dispatching the confirming Enter.
  if (event.isComposing || event.keyCode === 229 || event.repeat) return;
  event.preventDefault();
  void submit();
};
</script>

<template>
  <Modal :active="true" :close-disabled="creating" @close="emits('close')">
    <template #header>
      {{ $t("documentSpace.sections.createNew.title") }}
    </template>
    <template #body>
      <p v-if="createError" role="alert" class="save_error">
        {{ $t("error") }}
      </p>
      <input
        class="input"
        type="text"
        v-model="newName"
        :placeholder="$t('documentSpace.sections.createNew.placeholderName')"
        ref="inputElem"
        :disabled="creating"
        @keydown.enter="submitOnEnter"
      />
    </template>
    <template #footer>
      <div class="buttons">
        <button
          class="button secondary"
          :disabled="creating"
          @click="emits('close')"
        >
          {{ $t("cancel") }}
        </button>
        <button
          class="button primary"
          :class="{ disabled: !canSubmit }"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ $t("submit") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
.save_error {
  padding: 0.75em;
  color: var(--classic-error-text, #9f3a38);
  background: var(--classic-error-surface, #fff6f6);
}
</style>
